import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
} from "react";
import {
  AdminApiError,
  deactivateAdminRequestType,
  getAdminRequestTypeUsage,
  reactivateAdminRequestType,
  type RequestTypeAdminResponse,
  type RequestTypeUsageSummary,
} from "../../api/admin";
import "./RequestCategoryLifecyclePanel.css";

export type RequestCategoryLifecycleAction = "deactivate" | "reactivate";

export type RequestCategoryLifecyclePanelProps = {
  requestType: RequestTypeAdminResponse;
  action: RequestCategoryLifecycleAction;
  disabled: boolean;
  onCompleted: () => Promise<void>;
  onCancel: () => void;
};

type UsageState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready"; summary: RequestTypeUsageSummary }
  | { kind: "error"; message: string };

type UiState = {
  isSubmitting: boolean;
  usage: UsageState;
  formError: string;
};

function initialUi(): UiState {
  return {
    isSubmitting: false,
    usage: { kind: "idle" },
    formError: "",
  };
}

export default function RequestCategoryLifecyclePanel(
  props: RequestCategoryLifecyclePanelProps,
) {
  const { requestType, action, disabled, onCompleted, onCancel } = props;
  const [ui, setUi] = useState<UiState>(initialUi);
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const confirmButtonRef = useRef<HTMLButtonElement | null>(null);
  const isMountedRef = useRef(true);
  const usageInFlightRef = useRef(false);
  const submitInFlightRef = useRef(false);

  const headingId = useId();
  const descriptionId = useId();
  const usageHeadingId = useId();
  const usageListId = useId();
  const usageErrorId = useId();
  const formErrorId = useId();
  const readinessHintId = useId();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    headingRef.current?.focus();
  }, [action, requestType.id]);

  const verb = action === "deactivate" ? "Deactivate" : "Reactivate";
  const pastTense = action === "deactivate" ? "Deactivated" : "Reactivated";
  const isDeactivate = action === "deactivate";

  const clearNotifications = useCallback(() => {
    setUi((prev) => ({
      ...prev,
      formError: "",
    }));
  }, []);

  const loadUsage = useCallback(async () => {
    if (usageInFlightRef.current) {
      return;
    }
    if (!isMountedRef.current) {
      return;
    }
    usageInFlightRef.current = true;
    setUi((prev) => ({
      ...prev,
      usage: { kind: "loading" },
      formError: "",
    }));
    try {
      const summary = await getAdminRequestTypeUsage(requestType.id);
      if (!isMountedRef.current) {
        return;
      }
      setUi((prev) => ({
        ...prev,
        usage: { kind: "ready", summary },
      }));
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }
      if (error instanceof AdminApiError) {
        setUi((prev) => ({
          ...prev,
          usage: {
            kind: "error",
            message: error.detail || "Unable to load Request Category usage.",
          },
        }));
        return;
      }
      setUi((prev) => ({
        ...prev,
        usage: {
          kind: "error",
          message:
            "Unable to load Request Category usage. Please try again.",
        },
      }));
    } finally {
      usageInFlightRef.current = false;
    }
  }, [requestType.id]);

  useEffect(() => {
    if (!ui.isSubmitting) {
      void loadUsage();
    }
  }, [loadUsage, ui.isSubmitting]);

  const handleRetryUsage = useCallback(() => {
    if (disabled || ui.isSubmitting) {
      return;
    }
    setUi((prev) => ({ ...prev, formError: "" }));
    void loadUsage();
  }, [disabled, loadUsage, ui.isSubmitting]);

  const usageReady = ui.usage.kind === "ready";
  const usageSummary: RequestTypeUsageSummary | null =
    ui.usage.kind === "ready" ? ui.usage.summary : null;

  const audienceReady =
    requestType.is_internal_visible ||
    requestType.is_customer_visible ||
    requestType.is_partner_visible;
  const departmentReady =
    usageSummary !== null &&
    usageSummary.active_default_department &&
    usageSummary.default_department_routing_enabled;
  const reactivateReady = audienceReady && departmentReady;

  const handleConfirm = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (submitInFlightRef.current || disabled) {
        return;
      }
      clearNotifications();
      if (!usageReady) {
        return;
      }
      if (!isDeactivate && !reactivateReady) {
        return;
      }
      submitInFlightRef.current = true;
      setUi((prev) => ({ ...prev, isSubmitting: true }));
      try {
        if (isDeactivate) {
          await deactivateAdminRequestType(requestType.id);
        } else {
          await reactivateAdminRequestType(requestType.id);
        }
        if (!isMountedRef.current) {
          return;
        }
        setUi((prev) => ({
          ...prev,
          isSubmitting: false,
        }));
        await onCompleted();
      } catch (error) {
        if (!isMountedRef.current) {
          return;
        }
        if (error instanceof AdminApiError) {
          setUi((prev) => ({
            ...prev,
            isSubmitting: false,
            formError: error.detail,
          }));
          return;
        }
        setUi((prev) => ({
          ...prev,
          isSubmitting: false,
          formError:
            action === "deactivate"
              ? "Unable to deactivate the request category. Please try again."
              : "Unable to reactivate the request category. Please try again.",
        }));
      } finally {
        if (isMountedRef.current) {
          submitInFlightRef.current = false;
        }
      }
    },
    [
      action,
      clearNotifications,
      disabled,
      isDeactivate,
      onCompleted,
      reactivateReady,
      requestType.id,
      usageReady,
    ],
  );

  const cancelDisabled = ui.isSubmitting;
  const confirmDisabled =
    disabled || ui.isSubmitting || !usageReady || (!isDeactivate && !reactivateReady);

  return (
    <section
      className="request-category-lifecycle-panel"
      aria-labelledby={headingId}
      aria-describedby={descriptionId}
      id={`request-category-lifecycle-panel-${requestType.id}-${action}`}
    >
      <h2
        id={headingId}
        className="request-category-lifecycle-panel__title"
        ref={headingRef}
        tabIndex={-1}
      >
        {verb} Request Category
      </h2>
      <p
        id={descriptionId}
        className="request-category-lifecycle-panel__description"
      >
        {verb} the Request Category{" "}
        <strong>{requestType.name}</strong>. This action is final.
      </p>

      <section
        className="request-category-lifecycle-panel__context"
        aria-labelledby={`${headingId}-context`}
      >
        <h3
          id={`${headingId}-context`}
          className="request-category-lifecycle-panel__context-title"
        >
          Category context
        </h3>
        <dl
          className="request-category-lifecycle-panel__context-list"
        >
          <div className="request-category-lifecycle-panel__context-row">
            <dt className="request-category-lifecycle-panel__context-label">
              Code
            </dt>
            <dd className="request-category-lifecycle-panel__context-value">
              {requestType.code}
            </dd>
          </div>
          <div className="request-category-lifecycle-panel__context-row">
            <dt className="request-category-lifecycle-panel__context-label">
              Default Department
            </dt>
            <dd className="request-category-lifecycle-panel__context-value">
              {requestType.default_department_name ?? "Not set"}
            </dd>
          </div>
          <div className="request-category-lifecycle-panel__context-row">
            <dt className="request-category-lifecycle-panel__context-label">
              Status
            </dt>
            <dd className="request-category-lifecycle-panel__context-value">
              {requestType.is_active ? "Active" : "Inactive"}
            </dd>
          </div>
        </dl>
      </section>

      <section
        className="request-category-lifecycle-panel__usage"
        aria-labelledby={usageHeadingId}
        aria-describedby={readinessHintId}
      >
        <h3
          id={usageHeadingId}
          className="request-category-lifecycle-panel__usage-title"
        >
          Usage summary
        </h3>
        {ui.usage.kind === "loading" ? (
          <p
            className="request-category-lifecycle-panel__usage-loading"
            role="status"
          >
            Loading Request Category usage…
          </p>
        ) : null}
        {ui.usage.kind === "ready" ? (
          <dl
            id={usageListId}
            className="request-category-lifecycle-panel__usage-list"
          >
            <div className="request-category-lifecycle-panel__usage-row">
              <dt className="request-category-lifecycle-panel__usage-label">
                Active tickets
              </dt>
              <dd className="request-category-lifecycle-panel__usage-value">
                {ui.usage.summary.active_tickets}
              </dd>
            </div>
            <div className="request-category-lifecycle-panel__usage-row">
              <dt className="request-category-lifecycle-panel__usage-label">
                Open tickets
              </dt>
              <dd className="request-category-lifecycle-panel__usage-value">
                {ui.usage.summary.open_tickets}
              </dd>
            </div>
            <div className="request-category-lifecycle-panel__usage-row">
              <dt className="request-category-lifecycle-panel__usage-label">
                Total tickets
              </dt>
              <dd className="request-category-lifecycle-panel__usage-value">
                {ui.usage.summary.total_tickets}
              </dd>
            </div>
            <div className="request-category-lifecycle-panel__usage-row">
              <dt className="request-category-lifecycle-panel__usage-label">
                Default Department active
              </dt>
              <dd className="request-category-lifecycle-panel__usage-value">
                {ui.usage.summary.active_default_department ? "Yes" : "No"}
              </dd>
            </div>
            <div className="request-category-lifecycle-panel__usage-row">
              <dt className="request-category-lifecycle-panel__usage-label">
                Default Department routing enabled
              </dt>
              <dd className="request-category-lifecycle-panel__usage-value">
                {ui.usage.summary.default_department_routing_enabled
                  ? "Yes"
                  : "No"}
              </dd>
            </div>
          </dl>
        ) : null}
        {ui.usage.kind === "error" ? (
          <div className="request-category-lifecycle-panel__usage-error-wrap">
            <p
              id={usageErrorId}
              className="request-category-lifecycle-panel__usage-error"
              role="alert"
            >
              {ui.usage.message}
            </p>
            <button
              type="button"
              className="request-category-lifecycle-panel__retry"
              onClick={handleRetryUsage}
              disabled={disabled || ui.isSubmitting}
            >
              Retry
            </button>
          </div>
        ) : null}
        <p
          id={readinessHintId}
          className="request-category-lifecycle-panel__hint"
          aria-live="polite"
        >
          {isDeactivate
            ? "Ticket counts are informational. Deactivation may proceed even when tickets reference the category; historical tickets remain visible."
            : !audienceReady && !departmentReady
              ? "Reactivation requires at least one audience flag and an active routing-enabled default Department."
              : !audienceReady
                ? "Reactivation requires at least one audience flag to be enabled."
                : !departmentReady
                  ? "Default Department must be active and routing-enabled before reactivation can succeed."
                  : "All checks passed. Reactivation is allowed."}
        </p>
      </section>

      <form
        noValidate
        className="request-category-lifecycle-panel__form"
        onSubmit={handleConfirm}
      >
        {ui.formError.length > 0 ? (
          <p
            id={formErrorId}
            className="request-category-lifecycle-panel__form-error"
            role="alert"
          >
            {ui.formError}
          </p>
        ) : null}

        <div
          className="request-category-lifecycle-panel__actions"
          role="group"
          aria-labelledby={headingId}
        >
          <button
            ref={confirmButtonRef}
            type="submit"
            className={
              "request-category-lifecycle-panel__confirm" +
              (action === "reactivate"
                ? " request-category-lifecycle-panel__confirm--reactivate"
                : "")
            }
            disabled={confirmDisabled}
            aria-busy={ui.isSubmitting}
          >
            {ui.isSubmitting
              ? action === "deactivate"
                ? "Deactivating…"
                : "Reactivating…"
              : action === "deactivate"
                ? "Deactivate Request Category"
                : "Reactivate Request Category"}
          </button>
          <button
            type="button"
            className="request-category-lifecycle-panel__cancel"
            onClick={onCancel}
            disabled={cancelDisabled}
          >
            Cancel
          </button>
        </div>
        <span
          className="request-category-lifecycle-panel__visually-hidden"
          aria-live="polite"
        >
          {pastTense + " confirmation is final."}
        </span>
      </form>
    </section>
  );
}